import type { MetaDescriptor } from "react-router";
import { LIVE_LOCALES, DEFAULT_LOCALE, liveLocale } from "../i18n/locales";
import { instanceFor } from "../i18n/instances";
import { industryMapFor, industrySlugs } from "../content/industries";
import { postMapFor, postSlugs } from "../content/posts";
import { DEFAULT_SITE_URL } from "./site-url";

/**
 * Per-page SEO metadata, emitted at prerender via each route's `meta` export
 * (rendered by <Meta /> in root.tsx) — NOT via client useEffect, so crawlers
 * and social/AI bots get it in the first response.
 *
 * Copy is grounded in each page's actual h1 + lead (Belgium-default market).
 *
 * Story 4.5 (§6.1): `pageMeta(key, locale, slug?)` is locale-aware — it emits a
 * self-referential canonical, the full hreflang set across live locales where the
 * route exists (market/slug-gated), `x-default`→`nl`, and per-locale og:url/og:locale.
 * Title/description stay English here for now; their move into each locale's
 * `seo.json` is Story 4.7.
 */
const SITE_NAME = "Done-it";

/**
 * Real production domain (closes A5). Build-time configurable via `VITE_SITE_URL`
 * so the canonical can flip at the prod-domain cutover (G1) WITHOUT a code change:
 * set `VITE_SITE_URL` in the deploy env + rebuild. Default = where the NEW site
 * actually lives today (Railway) — deliberately NOT `done-it.app`, which still
 * serves the OLD site. Trailing slash stripped so `${SITE_URL}/x` never doubles.
 * The default lives in `./site-url` (DEFAULT_SITE_URL) so the build-time sitemap
 * generator shares the exact same value (Story 4.6) — one cutover point (G1).
 */
export const SITE_URL = (
  // `||` (not `??`) so a set-but-empty VITE_SITE_URL falls back to the default
  // rather than baking relative canonical/hreflang hrefs into the HTML.
  import.meta.env.VITE_SITE_URL || DEFAULT_SITE_URL
).replace(/\/$/, "");

/**
 * Canonical path per page — query variants (?type, ?slug) collapse to the base.
 * This is also the single source of truth for the set of page keys: `PageMetaKey`
 * is derived from it. Story 4.7 (§6.1) moved the title/description COPY out of a
 * `PAGE_META` literal here and into each locale's `seo.json` namespace — `pageMeta`
 * now resolves them per-locale via `instanceFor(locale).getFixedT(…, "seo")`, so a
 * Dutch page emits a Dutch title/description (with `en` fallback), not English.
 */
const PATHS = {
  home: "/",
  features: "/features",
  projectManagement: "/project-management",
  law2027: "/law-2027",
  pricing: "/pricing",
  industries: "/industries",
  industry: "/industry",
  resources: "/resources",
  trainings: "/trainings",
  gettingStarted: "/getting-started",
  blog: "/blog",
  legalTerms: "/legal/terms",
  legalPrivacy: "/legal/privacy",
  faq: "/faq",
  about: "/about",
  contact: "/contact",
  freeTrial: "/free-trial",
} as const;

export type PageMetaKey = keyof typeof PATHS;

/** Routes that exist only under belgium-market locales (mirrors `BELGIUM_ONLY`
 * in react-router.config.ts — keep in sync so hreflang never points at a URL the
 * prerender matrix didn't emit, e.g. `/en/law-2027`). */
const BELGIUM_ONLY_KEYS = new Set<PageMetaKey>(["law2027"]);

/** Per-slug routes → the slug function that decides where each slug is prerendered.
 * Same source of truth as the prerender matrix (NFR10): a Belgium-only post is in
 * `postSlugs("nl")` but not `postSlugs("en")`, so its hreflang set is nl-only. */
const SLUGS_FOR: Partial<Record<PageMetaKey, (code: string) => string[]>> = {
  industry: industrySlugs,
  blog: postSlugs,
};

/**
 * Build the per-locale title/description/canonical/hreflang/x-default/OG/Twitter
 * descriptors for a page (§6.1). `locale` comes from the route's `params.lang`;
 * `slug` from `params.slug` for the `industry`/`blog` detail routes.
 */
export function pageMeta(
  key: PageMetaKey,
  locale: string = DEFAULT_LOCALE,
  slug?: string,
): MetaDescriptor[] {
  const def = liveLocale(locale) ?? liveLocale(DEFAULT_LOCALE)!;
  // Title/description from the locale's `seo` catalog (§6.1). `getFixedT` reads the
  // locale's fixed instance synchronously (resources are bundled — see instances.ts)
  // so this works at prerender inside a route `meta` export, outside React. Missing
  // keys fall back to `en` (fallbackLng), never blank.
  const t = instanceFor(def.code).getFixedT(def.code, "seo");
  // Detail pages (blog post / industry) get their OWN title + description from the
  // content catalog, so each post/sector has a unique <title>/og:title instead of
  // the shared static `seo.<key>.title` (N2). Falls back to the static copy for the
  // bare listing or an unknown slug.
  let title = t(`${key}.title`);
  let description = t(`${key}.description`);
  if (slug) {
    if (key === "blog") {
      const post = postMapFor(def.code)[slug];
      if (post) {
        title = post.title;
        if (post.lead) description = post.lead;
      }
    } else if (key === "industry") {
      const industry = industryMapFor(def.code)[slug];
      if (industry) {
        title = industry.name;
        if (industry.headline) description = industry.headline;
      }
    }
  }

  // Page path under a locale prefix. Detail pages carry their slug; the bare
  // listing path ("/" only for home) otherwise.
  const base = PATHS[key];
  const sub = slug ? `${base === "/" ? "" : base}/${slug}` : base;
  const urlFor = (code: string) => `${SITE_URL}/${code}${sub === "/" ? "" : sub}`;

  // hreflang alternates: every LIVE locale (status === "live" already excludes
  // drafts — G2) where THIS route exists. For detail pages that's "the slug is in
  // that locale's prerendered set"; for static pages it's the market gate.
  // G2 review seam: when per-namespace review state lands (Story 4.10), AND a
  // `namespacesReviewed(L.code, key)` predicate into this filter.
  const slugFn = SLUGS_FOR[key];
  const alternates = LIVE_LOCALES.filter((L) =>
    slug && slugFn
      ? slugFn(L.code).includes(slug)
      : !BELGIUM_ONLY_KEYS.has(key) || L.market === "belgium",
  );

  const canonical = urlFor(def.code);
  // x-default → DEFAULT_LOCALE (nl), but ONLY when nl is actually in this route's
  // alternate set. Today nl carries every prerendered route (Belgium-only set ⊆ nl), so
  // the guard is always satisfied; it future-proofs the per-locale content split
  // (Story 4.8 / G5) where a route might no longer exist under nl — without it,
  // x-default would dangle at a URL the prerender matrix never emitted.
  const xDefault: MetaDescriptor[] = alternates.some((L) => L.code === DEFAULT_LOCALE)
    ? [{ tagName: "link", rel: "alternate", hreflang: "x-default", href: urlFor(DEFAULT_LOCALE) }]
    : [];
  return [
    { title },
    { name: "description", content: description },
    { tagName: "link", rel: "canonical", href: canonical },
    // Full hreflang set across live locales where this route exists.
    ...alternates.map((L) => ({
      tagName: "link" as const,
      rel: "alternate",
      hreflang: L.htmlLang,
      href: urlFor(L.code),
    })),
    ...xDefault,
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: canonical },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:locale", content: def.htmlLang },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
  ];
}
